// Renders the active workspace MFE remote. In this skeleton the workspace
// remotes (gaxl-web-platform-workspaces) don't exist yet, so load failures fall
// back to a placeholder describing what will mount here.

import React, { Suspense, useEffect, useMemo } from 'react';
import { useShellStore } from '../../shell/store/shellStore';
import { ErrorBoundary } from '../../common/components/ErrorBoundary';
import { Loader } from '../../common/components/Loader';
import { useShellBootstrap } from '../../common/components/ShellBootstrapContext';
import { loadWorkspaceRemote } from './loadRemote';
import { config } from '../../config';
import type { WorkspaceNavItem } from '../Navigation/workspaceCatalog';

interface WorkspaceOutletProps {
  workspace: WorkspaceNavItem;
}

const WorkspacePlaceholder: React.FC<{ workspace: WorkspaceNavItem }> = ({ workspace }) => (
  <div className="flex h-full flex-col items-center justify-center gap-2 text-text-secondary">
    <p className="text-lg font-medium text-text-primary">{workspace.label} workspace</p>
    <p className="text-sm">
      MFE remote <code>{workspace.id}</code> will mount here once
      <code className="mx-1">gaxl-web-platform-workspaces</code>
      is running.
    </p>
    {workspace.id === 'operational' && (
      <p className="text-xs text-text-tertiary">
        The Operational workspace hosts the Canvas Rendering Engine (Track 1 / Track 2).
      </p>
    )}
  </div>
);

export const WorkspaceOutlet: React.FC<WorkspaceOutletProps> = ({ workspace }) => {
  const setActiveWorkspace = useShellStore(state => state.setActiveWorkspace);
  const { catalogReady } = useShellBootstrap();

  useEffect(() => {
    setActiveWorkspace(workspace.id);
  }, [workspace.id, setActiveWorkspace]);

  const RemoteWorkspace = useMemo(() => {
    const remoteCfg = config.workspacesConfig.remotes.find(r => r.id === workspace.id);
    return React.lazy(async () => {
      if (!remoteCfg) return { default: () => <WorkspacePlaceholder workspace={workspace} /> };
      try {
        return await loadWorkspaceRemote(remoteCfg.id, remoteCfg.exposedModule);
      } catch {
        return { default: () => <WorkspacePlaceholder workspace={workspace} /> };
      }
    });
  }, [workspace]);

  // While the shell catalog overlay is up, suppress this Suspense fallback so
  // only one loader is visible on screen.
  const suspenseFallback = catalogReady
    ? <Loader label={`Loading ${workspace.label}…`} />
    : null;

  return (
    <ErrorBoundary fallback={<WorkspacePlaceholder workspace={workspace} />}>
      <Suspense fallback={suspenseFallback}>
        <RemoteWorkspace />
      </Suspense>
    </ErrorBoundary>
  );
};
